import { isValidSelectorString } from '@/utils/validations/selector.validation';
import { z } from 'zod';
import { ExtendedHotelRoomCategorySchema } from './HotelRoomCategoryFormData';
import { HotelPensionPricingSchema } from './HotelPensionPricingSchema';
import {
  DOUBLE_ROOM_COMBINATIONS,
  QUADRUPLE_ROOM_COMBINATIONS,
  TRIPLE_ROOM_COMBINATIONS,
} from '@/constants/HotelRoomTypes';
import { MarginRateSchema } from '@/types/common/margin-rate.schema';
import { AssociatedBenefitFrequencySchema } from '@/types/benefits/associated-benefit-frequency';
import { HotelSupplementBaseSchema } from './HotelSupplementSchema';

const ContactsSchema = z.object({
  coordinates: z
    .array(
      z.object({
        name: z.string().optional(),
        phoneNumber: z
          .string()
          .regex(
            /^\+?[0-9]+$/,
            'Le numéro de téléphone ne doit contenir que des chiffres ou un + au début',
          )
          .min(5, 'Le numéro de téléphone doit contenir au moins 5 caractères')
          .or(z.literal(''))
          .optional(),
      }),
    )
    .min(1),
  website: z.string().optional(),
  mainEmail: z.string().optional(),
  reservationsEmail: z.string().optional(),
  stockAlertEmail: z.string().optional(),
  secondaryEmails: z
    .array(z.object({ email: z.string().optional() }))
    .optional(),
});

export const ChildAgesSchema = z.object({
  childAges: z
    .array(
      z.object({
        childAgeFrom: z.coerce.number().min(0).optional(),
        childAgeTo: z.coerce.number().min(1).optional(),
      }),
    )
    .max(2)
    .optional(),
});

export type ChildAgesFormData = z.infer<typeof ChildAgesSchema>;

export const HotelSchemaBase = ExtendedHotelRoomCategorySchema.extend(
  ChildAgesSchema.shape,
).extend({
  name: z.string({ message: 'Le nom est requis' }),
  category: isValidSelectorString('La catégorie est requise').optional(),
  chain: isValidSelectorString('La chaîne est requise').optional(),
  supplier: isValidSelectorString('Le fournisseur est requis'),
  currency: isValidSelectorString('La devise est requise'),
  country: isValidSelectorString('Le pays est requis'),
  marginRate: MarginRateSchema.optional(),
  address: z.string().optional(),
  postalCode: z.coerce.string().optional(),
  city: isValidSelectorString('La ville est requise').optional(),
  latitude: z.coerce.number().min(-90).max(90).optional(),
  longitude: z.coerce.number().min(-180).max(180).optional(),
  contacts: ContactsSchema.optional(),
  mainImage: z.union([z.instanceof(Blob), z.string(), z.null()]).optional(),
  images: z.array(z.union([z.instanceof(Blob), z.string()])),
  shortDescriptionTitle: z.string().optional(),
  shortDescriptionBody: z.string().optional(),
  longDescriptionTitle: z.string().optional(),
  longDescriptionBody: z.string().optional(),
  acceptsChildPricing: z.enum(['true', 'false']).optional(),
  pricingType: z
    .enum(['byPerson', 'byRoom'], {
      message: 'Le type de tarif est requis',
    })
    .default('byRoom'),
  reducedPriceExists: z.enum(['true', 'false']).optional(),
  priceReductionType: z.enum(['newPrice', 'reduction']).optional(),
  pricingPensionType: isValidSelectorString(
    'Le type de pension est requis',
  ).optional(),
  shouldFillOtherPensionTypes: z.enum(['true', 'false']).optional(),
  areOtherPensionPricesCommon: z.enum(['true', 'false']).optional(),
  commonPensionPricings: z
    .array(HotelPensionPricingSchema)
    .optional()
    .default([]),
  shouldFillHotelSupplements: z.enum(['true', 'false']).optional(),
  hotelSupplements: z.array(HotelSupplementBaseSchema).optional().default([]),
  shouldAssociateBenefits: z.enum(['true', 'false']).default('false'),
  /** Tariffs pasted from the Excel grids (form preference, saved per hotel). */
  expertMode: z.enum(['true', 'false']).default('false'),
  associatedBenefits: z
    .array(
      z.object({
        benefitId: z.string(),
        benefit: z
          .object({
            id: z.string().optional(),
            name: z.string().nullable().optional(),
            code: z.number().nullable().optional(),
            type: z.string().optional(),
            country: z.string().nullable().optional(),
            supplier: z
              .object({
                id: z.string(),
                name: z.string().nullable(),
              })
              .nullable()
              .optional(),
            activity: z
              .object({
                activityType: z.string(),
                city: z.string().nullable(),
              })
              .nullable()
              .optional(),
          })
          .optional(),
        frequency: AssociatedBenefitFrequencySchema,
      }),
    )
    .default([]),
});

export const hotelSuperRefine = (
  data: z.infer<typeof HotelSchemaBase>,
  ctx: z.RefinementCtx,
) => {
  if (data.pricingType === 'byPerson') {
    if (data.reducedPriceExists == undefined)
      ctx.addIssue({
        code: 'invalid_type',
        expected: 'string',
        received: 'undefined',
        path: ['reducedPriceExists'],
        message: 'Le champ est requis',
      });
    else if (data.reducedPriceExists === 'true') {
      if (data.priceReductionType == undefined)
        ctx.addIssue({
          code: 'invalid_type',
          expected: 'string',
          received: 'undefined',
          path: ['priceReductionType'],
          message: 'Le champ est requis',
        });
    }
  }
  if (data.pricingType === 'byPerson' && data.acceptsChildPricing === 'true')
    data.hotelRoomCategories.forEach((hotelRoomCategory, index) => {
      if (
        !hotelRoomCategory.roomTypes.individual &&
        !hotelRoomCategory.roomTypes.double &&
        !hotelRoomCategory.roomTypes.triple &&
        !hotelRoomCategory.roomTypes.quadruple
      ) {
        ctx.addIssue({
          code: 'invalid_type',
          expected: 'string',
          received: 'undefined',
          path: ['hotelRoomCategories', index, 'roomTypes'],
          message: 'Il faut choisir au moins une option',
        });
      } else if (
        hotelRoomCategory.individualRoomOptions.length === 0 &&
        hotelRoomCategory.doubleRoomOptions.length === 0 &&
        hotelRoomCategory.tripleRoomOptions.length === 0 &&
        hotelRoomCategory.quadrupleRoomOptions.length === 0
      ) {
        if (hotelRoomCategory.roomTypes.individual)
          ctx.addIssue({
            code: 'invalid_type',
            expected: 'string',
            received: 'undefined',
            path: ['hotelRoomCategories', index, 'individualRoomOptions'],
            message: 'Veuillez sélectionner au moins un type de chambre',
          });
        else if (hotelRoomCategory.roomTypes.double)
          ctx.addIssue({
            code: 'invalid_type',
            expected: 'string',
            received: 'undefined',
            path: ['hotelRoomCategories', index, 'doubleRoomOptions'],
            message: 'Veuillez sélectionner au moins un type de chambre',
          });
        else if (hotelRoomCategory.roomTypes.triple)
          ctx.addIssue({
            code: 'invalid_type',
            expected: 'string',
            received: 'undefined',
            path: ['hotelRoomCategories', index, 'tripleRoomOptions'],
            message: 'Veuillez sélectionner au moins un type de chambre',
          });
        else if (hotelRoomCategory.roomTypes.quadruple)
          ctx.addIssue({
            code: 'invalid_type',
            expected: 'string',
            received: 'undefined',
            path: ['hotelRoomCategories', index, 'quadrupleRoomOptions'],
            message: 'Veuillez sélectionner au moins un type de chambre',
          });
      }
      if (hotelRoomCategory.roomTypes.double)
        hotelRoomCategory.doubleRoomOptions.forEach((option, indexOption) => {
          const optionCombination = DOUBLE_ROOM_COMBINATIONS[option];
          hotelRoomCategory.tarifs.forEach((tarif, indexTarif) => {
            if (
              optionCombination[1] > 0 &&
              tarif.pricePartitions.double &&
              tarif.pricePartitions.double.length > indexOption &&
              tarif.pricePartitions.double[indexOption].firstSpecialPrice ==
                undefined
            ) {
              ctx.addIssue({
                code: 'invalid_type',
                expected: 'number',
                received: 'undefined',
                path: [
                  'hotelRoomCategories',
                  index,
                  'tarifs',
                  indexTarif,
                  'pricePartitions',
                  'double',
                  indexOption,
                  'firstSpecialPrice',
                ],
                message: 'Le tarif est requis',
              });
            }
            if (
              optionCombination[2] > 0 &&
              tarif.pricePartitions.double &&
              tarif.pricePartitions.double.length > indexOption &&
              tarif.pricePartitions.double[indexOption].secondSpecialPrice ==
                undefined
            ) {
              ctx.addIssue({
                code: 'invalid_type',
                expected: 'number',
                received: 'undefined',
                path: [
                  'hotelRoomCategories',
                  index,
                  'tarifs',
                  indexTarif,
                  'pricePartitions',
                  'double',
                  indexOption,
                  'secondSpecialPrice',
                ],
                message: 'Le tarif est requis',
              });
            }
          });
        });
      if (hotelRoomCategory.roomTypes.triple)
        hotelRoomCategory.tripleRoomOptions.forEach((option, indexOption) => {
          const optionCombination = TRIPLE_ROOM_COMBINATIONS[option];
          hotelRoomCategory.tarifs.forEach((tarif, indexTarif) => {
            if (
              optionCombination[1] > 0 &&
              tarif.pricePartitions.triple &&
              tarif.pricePartitions.triple.length > indexOption &&
              tarif.pricePartitions.triple[indexOption].firstSpecialPrice ==
                undefined
            ) {
              ctx.addIssue({
                code: 'invalid_type',
                expected: 'number',
                received: 'undefined',
                path: [
                  'hotelRoomCategories',
                  index,
                  'tarifs',
                  indexTarif,
                  'pricePartitions',
                  'triple',
                  indexOption,
                  'firstSpecialPrice',
                ],
                message: 'Le tarif est requis',
              });
            }
            if (
              optionCombination[2] > 0 &&
              tarif.pricePartitions.triple &&
              tarif.pricePartitions.triple.length > indexOption &&
              tarif.pricePartitions.triple[indexOption].secondSpecialPrice ==
                undefined
            ) {
              ctx.addIssue({
                code: 'invalid_type',
                expected: 'number',
                received: 'undefined',
                path: [
                  'hotelRoomCategories',
                  index,
                  'tarifs',
                  indexTarif,
                  'pricePartitions',
                  'triple',
                  indexOption,
                  'secondSpecialPrice',
                ],
                message: 'Le tarif est requis',
              });
            }
          });
        });
      if (hotelRoomCategory.roomTypes.quadruple)
        hotelRoomCategory.quadrupleRoomOptions.forEach(
          (option, indexOption) => {
            const optionCombination = QUADRUPLE_ROOM_COMBINATIONS[option];
            hotelRoomCategory.tarifs.forEach((tarif, indexTarif) => {
              if (
                optionCombination[1] > 0 &&
                tarif.pricePartitions.quadruple &&
                tarif.pricePartitions.quadruple.length > indexOption &&
                tarif.pricePartitions.quadruple[indexOption]
                  .firstSpecialPrice == undefined
              ) {
                ctx.addIssue({
                  code: 'invalid_type',
                  expected: 'number',
                  received: 'undefined',
                  path: [
                    'hotelRoomCategories',
                    index,
                    'tarifs',
                    indexTarif,
                    'pricePartitions',
                    'quadruple',
                    indexOption,
                    'firstSpecialPrice',
                  ],
                  message: 'Le tarif est requis',
                });
              }
              if (
                optionCombination[2] > 0 &&
                tarif.pricePartitions.quadruple &&
                tarif.pricePartitions.quadruple.length > indexOption &&
                tarif.pricePartitions.quadruple[indexOption]
                  .secondSpecialPrice == undefined
              ) {
                ctx.addIssue({
                  code: 'invalid_type',
                  expected: 'number',
                  received: 'undefined',
                  path: [
                    'hotelRoomCategories',
                    index,
                    'tarifs',
                    indexTarif,
                    'pricePartitions',
                    'quadruple',
                    indexOption,
                    'secondSpecialPrice',
                  ],
                  message: 'Le tarif est requis',
                });
              }
            });
          },
        );
    });
  if (
    data.shouldFillHotelSupplements === 'true' &&
    data.hotelSupplements != null
  ) {
    data.hotelSupplements.forEach((supplement, i) => {
      if ((supplement.pensionTypes?.length ?? 0) < 1) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['hotelSupplements', i, 'pensionTypes'],
          message:
            'Au moins un type de pension doit être sélectionné pour ce supplément.',
        });
      }
    });
  }
};

export const HotelSchema = HotelSchemaBase.superRefine(hotelSuperRefine);

export type HotelFormData = z.infer<typeof HotelSchema>;
